import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../components/Icon.tsx';
import { Pagination } from '../components/Pagination.tsx';

const PAGE_SIZE = 10;
import {
  thanfeezuApi,
  type DecisionStatus,
  type ThanfeezuDecision,
  type ThanfeezuStats,
  type DecisionPatch,
} from '../features/thanfeezu/api.ts';
import { BodyToggle } from '../features/orgSettings/BodyToggle.tsx';
import { termsApi } from '../features/terms/api.ts';
import { presidentRole, type Body } from '../lib/body.ts';
import { useAuthStore } from '../store/auth.ts';
import { formatDvDate } from '../lib/dvDate.ts';
import { dv } from '../i18n/dv.ts';

const STATUS_ORDER: DecisionStatus[] = ['NOT_STARTED', 'ONGOING', 'ON_HOLD', 'IMPLEMENTED'];

// Theme-aligned decision-status colours — the SAME mapping used on the Dashboard
// (`TF_STATUS_COLOR`): ongoing = primary teal, implemented = success, on-hold =
// warning, not-started = outline. Feeds the seal ring, tally dots and status rail.
const STATUS_COLOR: Record<DecisionStatus, string> = {
  NOT_STARTED: 'hsl(var(--outline, var(--muted-foreground)))',
  ONGOING: 'hsl(var(--primary))',
  ON_HOLD: 'hsl(var(--warning))',
  IMPLEMENTED: 'hsl(var(--success))',
};

// Status "stamp" — reuses the app-wide .pill convention, tonally aligned to the
// status colours (teal / success / warning / neutral). Semantic colours only.
const STATUS_PILL: Record<DecisionStatus, string> = {
  NOT_STARTED: 'pill pill-neutral',
  ONGOING: 'pill pill-active',
  ON_HOLD: 'pill pill-pending',
  IMPLEMENTED: 'pill pill-success',
};

// Leading tonal circle (M3 list-item leading) per status.
const STATUS_ICON: Record<DecisionStatus, { name: string; cls: string }> = {
  NOT_STARTED: { name: 'schedule', cls: 'bg-[hsl(var(--surface-container-highest))] text-muted-foreground' },
  ONGOING: { name: 'pending', cls: 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]' },
  ON_HOLD: { name: 'pause_circle', cls: 'bg-[hsl(var(--warning-container))] text-[hsl(var(--on-warning-container))]' },
  IMPLEMENTED: { name: 'task_alt', cls: 'bg-[hsl(var(--success-container))] text-[hsl(var(--on-success-container))]' },
};

// YYYY-MM-DD for a date input / compact register date from an ISO string.
function toDateInput(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10);
}

// ─── Seal: implementation rate as an official-stamp ring ─────────────────────

function SealRing({ pct }: { pct: number }): JSX.Element {
  const SIZE = 164;
  const SW = 13;
  const R = (SIZE - SW) / 2;
  const C = 2 * Math.PI * R;
  const dash = (Math.max(0, Math.min(100, pct)) / 100) * C;

  return (
    <div className="relative mx-auto" style={{ width: SIZE, height: SIZE }}>
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden>
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          fill="none"
          stroke="hsl(var(--surface-container-highest))"
          strokeWidth={SW}
        />
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          fill="none"
          stroke={STATUS_COLOR.IMPLEMENTED}
          strokeWidth={SW}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${C}`}
          transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-bold tabular text-foreground" dir="ltr">
          {pct}%
        </span>
        <span className="status-label mt-0.5 text-[0.6rem]">{dv.thanfeezu.pctImplemented}</span>
      </div>
    </div>
  );
}

// ─── Summary register panel (ring + tally-as-filter + annotations) ───────────

function SummaryPanel({
  stats,
  filter,
  onFilter,
}: {
  stats: ThanfeezuStats;
  filter: DecisionStatus | 'ALL';
  onFilter: (f: DecisionStatus | 'ALL') => void;
}): JSX.Element {
  const rows: Array<{ key: DecisionStatus | 'ALL'; label: string; count: number; color?: string }> = [
    { key: 'ALL', label: dv.thanfeezu.all, count: stats.total },
    ...STATUS_ORDER.map((s) => ({
      key: s,
      label: dv.thanfeezu.status[s],
      count: stats.counts[s],
      color: STATUS_COLOR[s],
    })),
  ];

  return (
    <aside className="rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:gap-8">
        {/* Implementation ring — sits on the right in the RTL layout */}
        <div className="shrink-0 self-center">
          <SealRing pct={stats.pctImplemented} />
        </div>

        {/* Status tally (each row also filters) — fills the left side */}
        <div className="min-w-0 flex-1 space-y-4">
          <h2 className="status-label">{dv.thanfeezu.register}</h2>
          <ul className="grid gap-1">
            {rows.map((r) => {
              const active = filter === r.key;
              return (
                <li key={r.key}>
                  <button
                    type="button"
                    onClick={() => onFilter(r.key)}
                    aria-pressed={active}
                    className={[
                      'flex w-full items-center gap-2.5 rounded-full px-3 py-2 text-sm transition-colors',
                      active
                        ? 'bg-[hsl(var(--secondary-container))] font-medium text-[hsl(var(--on-secondary-container))]'
                        : 'text-muted-foreground hover:bg-[hsl(var(--surface-high))]',
                    ].join(' ')}
                  >
                    <span
                      aria-hidden
                      className="size-2.5 shrink-0 rounded-full"
                      style={{ background: r.color ?? 'hsl(var(--primary))' }}
                    />
                    <span className="flex-1 text-start">{r.label}</span>
                    <span className="tabular text-sm font-medium" dir="ltr">{r.count}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Average days from decision to implementation. */}
          {stats.avgDaysToImplement != null && (
            <p className="flex items-center justify-between gap-2 border-t border-black/[0.06] pt-3 text-xs text-muted-foreground">
              <span>{dv.thanfeezu.avgDays}</span>
              <span className="tabular font-medium text-foreground" dir="ltr">
                {stats.avgDaysToImplement} {dv.thanfeezu.days}
              </span>
            </p>
          )}
        </div>
      </div>
    </aside>
  );
}

// ─── A single docket entry (collapsed by default; expands to edit) ───────────

function DecisionDocket({
  d,
  body,
  canEdit,
}: {
  d: ThanfeezuDecision;
  body: Body;
  canEdit: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const [editStatus, setEditStatus] = useState<DecisionStatus>(d.status);
  const [editNote, setEditNote] = useState(d.note ?? '');
  const [editCompleted, setEditCompleted] = useState(() => toDateInput(d.completedDate));

  const mut = useMutation({
    mutationFn: (patch: DecisionPatch) => thanfeezuApi.update(d, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['thanfeezu', body] }),
  });

  const lead = STATUS_ICON[d.status];

  return (
    <article className="rounded-3xl bg-[hsl(var(--surface-container-low))] p-4 sm:p-5">
      {/* Header: leading tonal status icon, resolution number (file no.) + stamp */}
      <div className="flex items-start gap-4">
        <span className={`grid size-11 shrink-0 place-items-center rounded-full ${lead.cls}`}>
          <Icon name={lead.name} size={22} />
        </span>
        <div className="min-w-0 flex-1">
          <span className="status-label">{dv.thanfeezu.ninmunNumber}</span>
          <p className="tabular text-base font-medium text-foreground" dir="ltr">
            {d.ninmunNumber || '—'}
          </p>
        </div>
        <span className={`${STATUS_PILL[d.status]} shrink-0`}>{dv.thanfeezu.status[d.status]}</span>
      </div>

      {/* Resolution body */}
      <div className="mt-3 space-y-1.5">
        {d.proposalText && (
          <p className="text-[0.95rem] leading-relaxed text-foreground">{d.proposalText}</p>
        )}
        {d.decisionText && (
          <p className="text-sm leading-relaxed text-muted-foreground">
            <span className="status-label me-1 align-middle">{dv.thanfeezu.decided}</span>
            {d.decisionText}
          </p>
        )}
      </div>

      {/* Meeting reference */}
      <p className="mt-3 text-xs text-muted-foreground">
        {d.meetingTitle} · {formatDvDate(d.meetingDate)} · {dv.thanfeezu.masala} {d.itemNumber}
        {d.hasMultipleProposals && <> · {dv.thanfeezu.hushahelhun} {d.proposalNumber}</>}
      </p>

      {/* Timeline strip: decided → completed */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-black/[0.06] pt-3 text-xs">
        <TimelineStep label={dv.thanfeezu.decidedDate} value={toDateInput(d.ninmunPassedAt)} />
        {d.completedDate && (
          <TimelineStep
            label={dv.thanfeezu.completedDate}
            value={toDateInput(d.completedDate)}
            done
          />
        )}
      </div>

      {/* Progress note (read display) */}
      {d.note && !open && (
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{d.note}</p>
      )}

      {/* Editor toggle / inline editor */}
      {canEdit && (
        <div className="mt-3">
          {!open ? (
            <button type="button" onClick={() => setOpen(true)} className="btn-secondary btn-sm">
              <Icon name="edit" size={16} />
              {dv.common.edit}
            </button>
          ) : (
            <div className="space-y-3 border-t border-black/[0.06] pt-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="field-label">{dv.thanfeezu.statusLabel}</label>
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value as DecisionStatus)}
                    className="select w-full"
                  >
                    {STATUS_ORDER.map((s) => (
                      <option key={s} value={s}>{dv.thanfeezu.status[s]}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="field-label">{dv.thanfeezu.completedDate}</label>
                  <input
                    type="date"
                    dir="ltr"
                    value={editCompleted}
                    onChange={(e) => setEditCompleted(e.target.value)}
                    className="input w-full"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="field-label">{dv.thanfeezu.note}</label>
                <textarea
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  rows={3}
                  className="textarea w-full resize-y"
                />
              </div>

              {mut.isError && (
                <p className="text-sm text-destructive" role="alert">
                  {mut.error?.message ?? dv.common.error}
                </p>
              )}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    mut.mutate({
                      status: editStatus,
                      note: editNote.trim() || null,
                      completedDate: editCompleted || null,
                    })
                  }
                  disabled={mut.isPending}
                  className="btn-primary btn-sm"
                >
                  {mut.isPending ? dv.common.loading : dv.thanfeezu.save}
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={mut.isPending}
                  className="btn-ghost btn-sm"
                >
                  {dv.common.close}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

function TimelineStep({
  label,
  value,
  done,
}: {
  label: string;
  value: string;
  done?: boolean;
}): JSX.Element {
  const tone = done ? 'text-foreground' : 'text-muted-foreground';
  return (
    <span className={`flex items-center gap-1.5 ${tone}`}>
      <span className="status-label">{label}</span>
      <span className="tabular" dir="ltr">{value}</span>
    </span>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function Thanfeezu(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const [body, setBody] = useState<Body>('COUNCIL');
  const [filter, setFilter] = useState<DecisionStatus | 'ALL'>('ALL');

  // Term (dhauru) selector — scoped to the selected body. null = the body's
  // active term (backend default); a specific id, or 'all' for every term.
  const { data: terms } = useQuery({ queryKey: ['terms', body], queryFn: () => termsApi.list(body) });
  const [termSel, setTermSel] = useState<string | null>(null);
  useEffect(() => { setTermSel(null); }, [body]); // reset to active default when body changes
  const termId = termSel ?? undefined;

  const q = useQuery({
    queryKey: ['thanfeezu', body, termId ?? 'active'],
    queryFn: () => thanfeezuApi.list(body, termId),
  });

  const canEdit =
    user?.role === presidentRole(body) || user?.role === 'SECRETARY_GENERAL';
  const [search, setSearch] = useState('');
  const stats = q.data?.stats;
  const all = q.data?.decisions ?? [];
  const term = search.trim().toLowerCase();
  const decisions = (filter === 'ALL' ? all : all.filter((d) => d.status === filter)).filter((d) => {
    if (!term) return true;
    return [d.proposalText, d.decisionText, d.meetingTitle, d.ninmunNumber, d.note]
      .some((f) => f?.toLowerCase().includes(term));
  });

  // Client-side pagination; reset to page 1 whenever the body, filter or search changes.
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [body, filter, term, termId]);
  const pagedDecisions = decisions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="page space-y-6 lg:space-y-8">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold tracking-tight">{dv.thanfeezu.title}</h1>
        <div className="flex flex-wrap items-center gap-3">
          <BodyToggle body={body} onChange={setBody} />
          {terms && terms.length > 0 && (
            <select
              aria-label={dv.meetings.term}
              value={termSel ?? (terms.find((t) => t.isActive)?.id ?? 'all')}
              onChange={(e) => setTermSel(e.target.value)}
              className="select"
            >
              {terms.map((t) => (
                <option key={t.id} value={t.id}>{t.number} ވަނަ ދައުރު</option>
              ))}
              <option value="all">{dv.meetings.allTerms}</option>
            </select>
          )}
        </div>
      </header>

      {q.isLoading ? (
        <p className="rounded-3xl bg-[hsl(var(--chrome))] p-6 text-sm text-muted-foreground" role="status" aria-live="polite">
          {dv.common.loading}
        </p>
      ) : !stats || all.length === 0 ? (
        <div className="rounded-3xl bg-[hsl(var(--chrome))] p-10 text-center space-y-3">
          <div className="mx-auto inline-flex size-12 items-center justify-center rounded-full bg-[hsl(var(--primary-container))] text-[hsl(var(--on-primary-container))]">
            <Icon name="fact_check" size={22} />
          </div>
          <p className="text-sm text-muted-foreground">{dv.thanfeezu.empty}</p>
        </div>
      ) : (
        <div className="space-y-6">
          <SummaryPanel stats={stats} filter={filter} onFilter={setFilter} />

          <section className="space-y-3">
            <div className="relative">
              <Icon
                name="search"
                size={18}
                className="pointer-events-none absolute start-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={dv.thanfeezu.search}
                className="input w-full ps-11"
              />
            </div>
            <div className="section-heading">
              <h2 className="status-label">{dv.thanfeezu.decisions}</h2>
              <span className="tabular text-muted-foreground" dir="ltr">
                {decisions.length}
              </span>
            </div>

            {decisions.length === 0 ? (
              <p className="rounded-3xl bg-[hsl(var(--chrome))] p-8 text-center text-sm text-muted-foreground">
                {dv.thanfeezu.emptyFilter}
              </p>
            ) : (
              <>
                {/* Always a single column — reads best full-width. */}
                <ul className="grid grid-cols-1 items-start gap-3">
                  {pagedDecisions.map((d) => (
                    <li
                      key={`${d.yaumiyyaId}-${d.agendaItemId}-${d.proposalIndex}-${d.updatedAt ?? ''}`}
                    >
                      <DecisionDocket d={d} body={body} canEdit={canEdit} />
                    </li>
                  ))}
                </ul>
                <Pagination page={page} pageSize={PAGE_SIZE} total={decisions.length} onChange={setPage} />
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
